import { FormArray, FormGroup } from "@angular/forms";
export { dateInRange, inDay } from '@core/domain/availability';

export function toCapitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}


export function isHourInSchedule(
	dias: { horaInicio: string | null; horaFin: string | null; habilitado?: boolean | null; nombreDia?: string | null }[],
	currentTime: number
): boolean {
	const parseTime = (timeStr: string | null | undefined): number | null => {
		if (!timeStr) return null;

		const trimmed = timeStr.trim().toUpperCase();

		const isPM = trimmed.includes('PM');
		const isAM = trimmed.includes('AM');
		const clean = trimmed.replace(/AM|PM/g, '').trim();

		const parts = clean.split(':');
		if (parts.length < 2) return null;

		let hours = Number(parts[0]);
		const minutes = Number(parts[1]);

		if (isNaN(hours) || isNaN(minutes)) return null;

		if (isPM && hours !== 12) hours += 12;
		if (isAM && hours === 12) hours = 0;

		return hours * 100 + minutes;
	};

	return dias.some(dia => {
		if (dia.habilitado === false) return false;
		const openingTime = parseTime(dia.horaInicio || '');
		const closingTime = parseTime(dia.horaFin || '');
		if (openingTime === null || closingTime === null) return false;
		if (closingTime < openingTime) {
			return currentTime >= openingTime || currentTime <= closingTime;
		} else {
			return currentTime >= openingTime && currentTime <= closingTime;
		}
	});
}


export function parseUSAddress(address: string): {
  direccion1: string;
  direccion2: string;
  localidad: string;
} {
  // Limpiar la dirección y dividir por comas
  const cleanAddress = address.trim();
  const parts = cleanAddress.split(',').map(part => part.trim());

  let direccion1 = 'N/A';
  let direccion2 = 'N/A';
  let localidad = 'N/A';

  if (parts.length >= 3) {
    // Caso: "Miami Beach Boardwalk, Miami Beach, FL 33140, USA"
    direccion1 = parts[0]; // "Miami Beach Boardwalk"
    direccion2 = parts[1]; // "Miami Beach"
    localidad = parts.slice(2).join(', '); // "FL 33140, USA"
  } else if (parts.length === 2) {
    // Caso: "Miami Beach, FL 33140"
    direccion1 = parts[0]; // "Miami Beach"
    localidad = parts[1]; // "FL 33140"
  } else if (parts.length === 1) {
    // Caso: solo una parte
    direccion1 = parts[0];
  }

  return {
    direccion1,
    direccion2,
    localidad
  };
}

export function getLocalidadFromAddress(address: string): string {
  // Limpiar la dirección y dividir por comas
  const cleanAddress = address.trim();
  const parts = cleanAddress.split(',').map(part => part.trim());

  // Buscar la parte que contiene el estado (FL, CA, NY, etc.)
  const statePattern = /\b[A-Z]{2}\s+\d{5}/; // Patrón para estado + código postal

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (statePattern.test(part)) {
      // Encontrar la parte que contiene el estado y código postal
      const stateIndex = i;

      // Si hay una parte antes del estado, esa es la ciudad
      if (stateIndex > 0) {
        return parts[stateIndex - 1];
      }

      // Si no hay parte anterior, buscar en la misma parte
      const cityMatch = part.match(/^(.+?)\s+[A-Z]{2}\s+\d{5}/);
      if (cityMatch) {
        return cityMatch[1].trim();
      }
    }
  }

  // Fallback: si no encontramos el patrón de estado, tomar la segunda parte
  if (parts.length >= 2) {
    return parts[1];
  }

  // Si solo hay una parte, devolverla
  if (parts.length === 1) {
    return parts[0];
  }

  return 'N/A';
}

export async function getLocalidadFromGoogleMaps(address: string, apiKey: string): Promise<string> {
  try {
    // Construir la URL de la Geocoding API
    const encodedAddress = encodeURIComponent(address);
    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`;

    // Hacer la petición a la API
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'OK' && data.results.length > 0) {
      const result = data.results[0];

      // Buscar el componente de tipo "locality" (ciudad)
      const localityComponent = result.address_components.find(
        (component: { types: string[]; long_name: string }) => component.types.includes('locality')
      );

      if (localityComponent) {
        return localityComponent.long_name;
      }

      // Fallback: buscar "administrative_area_level_1" (estado)
      const stateComponent = result.address_components.find(
        (component: { types: string[]; long_name: string }) => component.types.includes('administrative_area_level_1')
      );

      if (stateComponent) {
        return stateComponent.long_name;
      }
    }

    return 'N/A';
  } catch (error) {
    console.error('Error al obtener localidad desde Google Maps:', error);
    return 'N/A';
  }
}

// Función alternativa usando la API de Places (más precisa para direcciones específicas)
export async function getLocalidadFromGooglePlaces(address: string, apiKey: string): Promise<string> {
  try {
    // Construir la URL de la Places API
    const encodedAddress = encodeURIComponent(address);
    const url = `https://maps.googleapis.com/maps/api/place/findplacefromtext/json?input=${encodedAddress}&inputtype=textquery&fields=formatted_address,address_components&key=${apiKey}`;

    // Hacer la petición a la API
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'OK' && data.candidates.length > 0) {
      const candidate = data.candidates[0];

      // Buscar el componente de tipo "locality"
      const localityComponent = candidate.address_components.find(
        (component: { types: string[]; long_name: string }) => component.types.includes('locality')
      );

      if (localityComponent) {
        return localityComponent.long_name;
      }
    }

    return 'N/A';
  } catch (error) {
    console.error('Error al obtener localidad desde Google Places:', error);
    return 'N/A';
  }
}

export function isValidIanaTimeZone(tz: string): boolean {
  const argentinaTimeZones: string[] = [
    'America/Argentina/Buenos_Aires',
    'America/Argentina/Catamarca',
    'America/Argentina/Cordoba',
    'America/Argentina/Jujuy',
    'America/Argentina/La_Rioja',
    'America/Argentina/Mendoza',
    'America/Argentina/Rio_Gallegos',
    'America/Argentina/Salta',
    'America/Argentina/San_Juan',
    'America/Argentina/San_Luis',
    'America/Argentina/Tucuman',
    'America/Argentina/Ushuaia',
    'America/Buenos_Aires',
    'America/Cordoba'
  ];

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return argentinaTimeZones.includes(tz);
  } catch (_) {
    return false;
  }
}

export function isSameCountry(c1?: string | null, c2?: string | null): boolean {
  if (!c1 || !c2) return true;

  const norm = (s: string) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "").trim();
  const n1 = norm(c1);
  const n2 = norm(c2);

  if (!n1 || !n2 || n1 === n2) return true;

  const aliases: string[][] = [
    ['us', 'usa', 'unitedstates', 'estadosunidos', 'eeuu'],
    ['ec', 'ecuador'],
    ['ar', 'argentina'],
    ['mx', 'mexico'],
    ['co', 'colombia'],
    ['pe', 'peru'],
    ['cl', 'chile'],
    ['pa', 'panama'],
    ['cr', 'costarica'],
    ['do', 'republicadominicana', 'dominicanrepublic'],
    ['gt', 'guatemala'],
    ['hn', 'honduras'],
    ['sv', 'elsalvador'],
    ['ni', 'nicaragua'],
    ['py', 'paraguay'],
    ['uy', 'uruguay'],
    ['bo', 'bolivia'],
    ['es', 'spain', 'espana'],
  ];

  for (const group of aliases) {
    const hasN1 = group.includes(n1);
    const hasN2 = group.includes(n2);
    if (hasN1 && hasN2) return true;
    if (hasN1 || hasN2) return false;
  }

  return false;
}

export function markFormGroupTouched(formGroup: FormGroup | FormArray, validate = false): void {
  Object.values((formGroup?.controls ?? [])).forEach(control => {
    if (control instanceof FormGroup) {
      // Si es un FormGroup, llamamos recursivamente a la función
      markFormGroupTouched(control, validate);
    } else if (control instanceof FormArray) {
      // Si es un FormArray, iteramos sobre sus controles
      markFormGroupTouched(control, validate);
    } else {
      // Si es un FormControl, lo marcamos como "touched" y "dirty"
      
      if (validate) {
        control.markAsTouched();
        control.markAsDirty();
        control.updateValueAndValidity();
      }
      
    }
  });
}
